import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Reusable multi-select dropdown with checkboxes.
 *
 * Props:
 *  - options:      [{ value: string, label: string }]
 *  - selected:     string[]           (array of selected values)
 *  - onChange:     (string[]) => void
 *  - placeholder:  string             (e.g. "All Countries")
 *  - testId:       string             (optional data-testid)
 */
const MultiSelectDropdown = ({ options, selected, onChange, placeholder = 'All', testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const clearAll = () => onChange([]);

  // Display text
  const displayText =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? options.find((o) => o.value === selected[0])?.label || selected[0]
        : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input-base text-sm w-full flex items-center justify-between gap-2 text-left"
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${selected.length === 0 ? 'text-slate-500' : 'text-slate-900'}`}>
          {displayText}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {/* Search — only worth showing on long lists (e.g. campaign names) */}
          {options.length > 8 && (
            <div className="sticky top-0 bg-white px-2 pt-2 pb-1 border-b border-slate-100">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>
          )}
          {/* "All" / clear option */}
          <button
            type="button"
            onClick={clearAll}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
              selected.length === 0 ? 'text-indigo-600 font-medium' : 'text-slate-700'
            }`}
            data-testid={testId ? `${testId}-option-all` : undefined}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
              selected.length === 0
                ? 'bg-indigo-600 border-indigo-600'
                : 'border-slate-300'
            }`}>
              {selected.length === 0 && <Check className="w-3 h-3 text-white" />}
            </span>
            {placeholder}
          </button>

          {options
            .filter((opt) => !query || (opt.label || '').toLowerCase().includes(query.toLowerCase()))
            .map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  checked ? 'text-indigo-600 font-medium' : 'text-slate-700'
                }`}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                  checked
                    ? 'bg-indigo-600 border-indigo-600'
                    : 'border-slate-300'
                }`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
